import { useSyncExternalStore, type MouseEvent, type ReactElement } from "react";
import { HoverWords } from "../bar/hover-words.tsx";
import { FlagMark, Glyph, HudFrame, logical } from "../../ui/kit/index.ts";
import { HUD_SLOTS, resolveSlot, type HudArea } from "../hud/slots.ts";
import { say } from "../../words.ts";
import { healthFill } from "../../ui/kit/index.ts";
import { countLine, frameRows, type FrameRow } from "./rows.ts";
import type { PartySource } from "./store.ts";

/**
 * The party frame (ADR-101 decision 23; the design's `Party` card in `ui_kits/surfaces/CombatHud.jsx`, a visual
 * reference): in `HUD_SLOTS.party`, a HUD frame of one row for every seat in place order, the player's included.
 * A row holds the leader's bookmark where the seat leads, the character's first name, and under it "Offline" for an
 * offline seat or "Elsewhere · no data" for an online member on another map than the player's; a member on the
 * player's map shows its flag mark and a health bar from the last `partyVitals` frame, "Dead" for a dead one and its name alone until a frame names it. A dot in the member's map colour (`partyColourOf` of its place) stands before every name. The count line closes the frame:
 * "4/6", and "6/6 · full" at six. A party of one or none draws nothing.
 *
 * Every row is a button that opens the player menu (a click, Enter or a right click): the player's own opens Leave,
 * every other the menu of that character, with the leader's Make leader, Can invite and Remove among it. A press on
 * a row keeps focus where it was, and the menu opens beside the frame. Every other part of the frame takes no pointer events.
 */

/**
 * The padding inside the frame's edge, in logical pixels: the design's 1.
 */
export const PARTY_PADDING = 1;

/**
 * The health bar's size in logical pixels: the design's 78 by 4, border included.
 */
export const PARTY_BAR_WIDTH = 78;
export const PARTY_BAR_HEIGHT = 4;

/**
 * How far right of a row the menu opens, in client CSS pixels.
 */
const MENU_GAP = 4;

/**
 * One row's contents: the leader's bookmark, the first name, and the row's state under it.
 *
 * @param props The row.
 * @returns The contents.
 */
function RowBody({ row }: { readonly row: FrameRow }): ReactElement {
  return (
    <>
      <span className="ot-party__head" data-party="head">
        <FlagMark state={row.vitals.kind === "alive" || row.vitals.kind === "dead" ? row.vitals.flag : "protected"} locked={row.vitals.kind === "alive" || row.vitals.kind === "dead" ? row.vitals.locked : false} size={8} />
        {row.leader ? (
          <span className="ot-party__leader" data-party="leader">
            <Glyph name="bookmark" label={say("party.frames.leader")} />
          </span>
        ) : null}
        <span className="ot-party__dot" data-party="colour" data-colour={row.colour} style={{ backgroundColor: `var(--ot-${row.colour})` }} role="img" aria-label={say("party.frames.colour", { name: row.fullName })} />
        <span className={row.where === "offline" ? "ot-party__name is-away" : "ot-party__name"} data-party="name">
          {row.name}
        </span>
      </span>
      {row.where === "offline" ? (
        <span className="ot-party__state" data-party="state">
          {say("party.frames.offline")}
        </span>
      ) : row.where === "elsewhere" ? (
        <span className="ot-party__state" data-party="state">
          {say("party.frames.elsewhere")}
        </span>
      ) : row.vitals.kind === "dead" ? (
        <span className="ot-party__state" data-party="state">
          {say("party.frames.dead")}
        </span>
      ) : row.vitals.kind === "alive" ? (
        <span className="ot-hud-bar ot-hud-bar--health ot-party__bar" data-party="health" role="meter" aria-label={say("party.frames.health", { name: row.fullName })} aria-valuemin={0} aria-valuemax={255} aria-valuenow={row.vitals.health} style={{ width: logical(PARTY_BAR_WIDTH), height: logical(PARTY_BAR_HEIGHT) }}>
          <span className="ot-hud-bar__fill" data-party="health-fill" data-fill={healthFill(row.vitals.health, 255, PARTY_BAR_WIDTH - 2)} style={{ width: logical(healthFill(row.vitals.health, 255, PARTY_BAR_WIDTH - 2)) }} />
        </span>
      ) : null}
    </>
  );
}

/**
 * The frame in its slot.
 *
 * @param props The `area` it is laid out on and the `party` it reads and opens its menu through.
 * @returns The frame, or null while the party seats fewer than two.
 */
export function PartyFrames({ area, party }: { readonly area: HudArea; readonly party: PartySource }): ReactElement | null {
  const snapshot = useSyncExternalStore(party.subscribe, party.getSnapshot, party.getSnapshot);
  const rows = frameRows(snapshot);
  if (rows.length === 0) {
    return null;
  }
  const slot = resolveSlot(HUD_SLOTS.party, area);
  const open = (row: FrameRow, event: MouseEvent<HTMLButtonElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    party.openMenu({ firstName: snapshot.members.find((member) => member.place === row.place)?.firstName ?? row.name, familyName: snapshot.members.find((member) => member.place === row.place)?.familyName ?? "", x: box.right + MENU_GAP, y: box.top });
  };
  return (
    <div className="ot-hud-slot ot-party" data-hud-slot="party" role="group" aria-label={say("party.frames.panel")} style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width) }}>
      <HudFrame padding={PARTY_PADDING} width={slot.width}>
        {rows.map((row) => (
          <HoverWords key={row.place} words={row.fullName} block>
            <button
              type="button"
              className="ot-party__row is-button"
              data-party="row"
              data-place={row.place}
              aria-haspopup="menu"
              aria-label={say(row.leader ? "party.frames.leaderOpen" : "party.frames.memberOpen", { name: row.fullName })}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => open(row, event)}
              onContextMenu={(event) => open(row, event)}
            >
              <RowBody row={row} />
            </button>
          </HoverWords>
        ))}
        <div className="ot-party__count" data-party="count">
          {countLine(rows.length)}
        </div>
      </HudFrame>
    </div>
  );
}
